import React, { useState, useEffect } from 'react';
import { Content } from '../Settings.style';
import {
  MenuItem,
  Select,
  TextField,
  InputLabel,
  FormControl,
  Typography,
  CircularProgress,
  Divider,
  Box,
} from '@material-ui/core';
import {
  useGetApplicationsMainForSelectLazyQuery,
  useGetSchoolGradesQuery,
} from '../../../../generated/graphql';
import { API } from '../../../../core/axios/client';
import { ButtonCustom } from '../../../shared/Style/Style';

const PAGE_SIZE = 10;

export const TestGenerator = () => {
  const applicationId = process.env.REACT_APP_AR_BOOK_APP_ID;
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [number, setNumber] = useState('');
  const [numberError, setNumberError] = useState(false);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fetchSubjects, { loading: subjectsLoading }] =
    useGetApplicationsMainForSelectLazyQuery();
  const { data: gradesData, loading: gradesLoading } =
    useGetSchoolGradesQuery();

  // Initial load
  useEffect(() => {
    setSubjects([]);
    setSkip(0);
    setHasMore(true);
    fetchSubjects({
      variables: {
        applicationId,
        isPublished: true,
        skip: 0,
        limit: PAGE_SIZE,
      },
      onCompleted: (data) => {
        setSubjects(data?.getApplicationsMain?.applicationsMain || []);
        setHasMore(
          (data?.getApplicationsMain?.applicationsMain?.length || 0) ===
            PAGE_SIZE
        );
        setSkip(PAGE_SIZE);
      },
    });
    // eslint-disable-next-line
  }, [applicationId]);

  // Load more on scroll
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      !loadingMore &&
      hasMore &&
      scrollHeight - scrollTop - clientHeight < 50
    ) {
      setLoadingMore(true);
      fetchSubjects({
        variables: { applicationId, isPublished: true, skip, limit: PAGE_SIZE },
        onCompleted: (data) => {
          const newSubjects = data?.getApplicationsMain?.applicationsMain || [];
          setSubjects((prev) => [...prev, ...newSubjects]);
          setHasMore(newSubjects.length === PAGE_SIZE);
          setSkip((prev) => prev + PAGE_SIZE);
          setLoadingMore(false);
        },
      });
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,3}$/.test(value)) {
      setNumber(value);
      const num = Number(value);
      setNumberError(!(num >= 1 && num <= 100));
    }
  };

  const handleAdd = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await API.post('/ai-generator/generate-test-lessons', {
        count: Number(number),
        subjectId: selectedSubject,
        gradeId: selectedGrade,
      });
      setSuccess(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Сталася помилка');
    } finally {
      setLoading(false);
    }
  };

  const isAddDisabled =
    !selectedSubject || !selectedGrade || !number || numberError || loading;

  return (
    <Content>
      <Box p={3}>
        <Typography
          variant="h6"
          style={{ marginBottom: 12, textAlign: 'center', fontWeight: 600 }}
        >
          Генерація тестів
        </Typography>
        <Divider style={{ marginBottom: 24 }} />
        <FormControl fullWidth style={{ marginBottom: 24 }}>
          <InputLabel id="subject-label">Предмет</InputLabel>
          <Select
            labelId="subject-label"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value as string)}
            disabled={subjectsLoading}
            MenuProps={{
              PaperProps: {
                style: { maxHeight: 300, overflowY: 'auto' },
                onScroll: handleScroll,
              },
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              getContentAnchorEl: null,
            }}
          >
            {subjects.map(
              (subject) =>
                subject && (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                )
            )}
            {loadingMore && (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <FormControl fullWidth style={{ marginBottom: 24 }}>
          <InputLabel id="grade-label">Клас</InputLabel>
          <Select
            labelId="grade-label"
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value as string)}
            disabled={gradesLoading}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              getContentAnchorEl: null,
            }}
          >
            {gradesData?.getSchoolGrades?.map(
              (grade) =>
                grade && (
                  <MenuItem key={grade.id} value={grade.id}>
                    {grade.name}
                  </MenuItem>
                )
            )}
          </Select>
        </FormControl>
        <TextField
          label="Кількість уроків"
          type="number"
          value={number}
          onChange={handleNumberChange}
          error={numberError}
          helperText={numberError ? 'Введіть число від 1 до 100' : ''}
          inputProps={{ min: 1, max: 100 }}
          fullWidth
          style={{ marginBottom: 32 }}
        />
        <Box display="flex" justifyContent="center">
          <ButtonCustom
          isActive={!isAddDisabled}
            onClick={handleAdd}
            disabled={isAddDisabled}
            style={{ minWidth: 120, fontWeight: 600, fontSize: 16 }}
            main_color="black"
          >
            {loading ? (
              <CircularProgress size={22} style={{ color: '#fff' }} />
            ) : (
              'Додати'
            )}
          </ButtonCustom>
        </Box>
        {success && (
          <Typography
            style={{ color: 'green', marginTop: 16, textAlign: 'center' }}
          >
            Успішно згенеровано!
          </Typography>
        )}
        {error && (
          <Typography
            style={{ color: 'red', marginTop: 16, textAlign: 'center' }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </Content>
  );
};
